import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { FormGroupQuestion } from '../components/re-orderable-theme/question-node/question-node.component';
import { FormGroupTheme } from '../components/re-orderable-theme/re-orderable-theme.component';
import { IDiscussionGuideQuestion } from '../models/data-capture.models';

@Injectable({
  providedIn: 'root',
})
export class QuestionHierarchyService {
  recursiveFindAndGroupChildren(
    questions: IDiscussionGuideQuestion[],
    parentId: string
  ): FormArray<FormGroupQuestion> {
    const children = questions.filter((q) => q.parent_question_id === parentId);

    return new FormArray<FormGroupQuestion>(
      children.map(
        (c) =>
          new FormGroup({
            question_id: new FormControl<string>(c.question_id),
            question: new FormControl<string>(c.question),
            theme: new FormControl<string>(c.theme),
            label: new FormControl<string>(c.label),
            parent_question_id: new FormControl<string | null>(
              c.parent_question_id || null
            ),
            children: this.recursiveFindAndGroupChildren(
              questions,
              c.question_id
            ),
          })
      )
    );
  }

  flattenThemes(themesControl: FormArray<FormGroupTheme>) {
    const discussionGuide: IDiscussionGuideQuestion[] = [];

    themesControl.controls.forEach((themeGroup) => {
      const themeName = themeGroup.controls.theme.value;

      themeGroup.controls.questions.controls.forEach((questionGroup) => {
        const questionId = questionGroup.controls.question_id.value;
        const questionText = questionGroup.controls.question.value;
        const label = questionGroup.controls.label.value;
        const childrenForm = questionGroup.controls.children;

        discussionGuide.push({
          question_id: questionId,
          question: questionText,
          theme: themeName,
          label,
          parent_question_id: null,
        });

        const children = this.getRecursiveChildren(
          questionId,
          childrenForm,
          themeName
        );

        children.forEach((child) => {
          discussionGuide.push({ ...child, theme: themeName });
        });
      });
    });

    return discussionGuide;
  }

  private getRecursiveChildren(
    questionId: string,
    childrenForm: FormArray<FormGroupQuestion>,
    theme: string
  ): IDiscussionGuideQuestion[] {
    return childrenForm.controls.flatMap((childGroup) => {
      const childQuestionId = childGroup.controls.question_id.value;
      const childQuestionText = childGroup.controls.question.value;
      const label = childGroup.controls.label.value;
      const grandChildrenForm = childGroup.controls.children;

      const childQuestion: IDiscussionGuideQuestion = {
        question_id: childQuestionId,
        question: childQuestionText,
        theme,
        label,
        parent_question_id: questionId,
      };

      const grandChildren = this.getRecursiveChildren(
        childQuestionId,
        grandChildrenForm,
        theme
      );

      return [childQuestion, ...grandChildren];
    });
  }
}
